<template>
  <div style="position: relative; display: block">
    <canvas :id="canvaID"></canvas>
  </div>
</template>
<script>
import Chart from "chart.js";
import "chartjs-plugin-annotation";

const RESERVOIR_COLORS = [
  "#4092FF", "#002E71", "#00ACC1", "#204980", "#007BFF",
  "#1976D2", "#009688", "#303F9F", "#0057B7", "#607D8B",
];
const ESTIMATED_POINT_COLOR = "#FF9800";

export default {
  props: {
    title: { type: String, default: null },
    labels: { type: Array, default: () => [] },
    readings: { type: Array, default: () => [] },
  },
  data() {
    return {
      id: null,
      canvaID: null,
      colorLineAverage: "#3bf2ff",
      gradientBg: null,
      myChart: null,
      data: {
        labels: [],
        datasets: [],
      },
    };
  },
  created() {
    this.id = this._uid;
    this.canvaID = `graphic-consumption-${this.id}`;
  },
  mounted() {
    this.prepareDatasets();
    this.render();
  },
  methods: {
    resolveHexColor(rawColor, fallback = "#4092FF") {
      if (typeof rawColor === "string" && rawColor.startsWith("#")) return rawColor;
      if (Array.isArray(rawColor)) {
        const hexFromArray = rawColor.find(
          (color) => typeof color === "string" && color.startsWith("#")
        );
        if (hexFromArray) return hexFromArray;
      }
      return fallback;
    },

    applyPointColors(pointColors, lineColor) {
      if (!Array.isArray(pointColors)) return lineColor;
      return pointColors.map((pointColor) => (
        pointColor === ESTIMATED_POINT_COLOR ? ESTIMATED_POINT_COLOR : lineColor
      ));
    },

    prepareDatasets() {
      this.data.datasets = [];

      this.readings.forEach((reading, readingIndex) => {
        reading.datasets.forEach((dataset, datasetIndex) => {
          const fallbackColor = RESERVOIR_COLORS[readingIndex % RESERVOIR_COLORS.length];
          const lineColor = this.resolveHexColor(dataset.borderColor, fallbackColor);
          const isEstimated = dataset.borderDash && dataset.borderDash.length > 0;
          let backgroundColor = dataset.backgroundColor || "transparent";

          if (!isEstimated && dataset.fill !== false) {
            const ctx = document.getElementById(this.canvaID)?.getContext("2d");
            if (ctx) {
              const gradient = ctx.createLinearGradient(0, 0, 0, 320);
              gradient.addColorStop(0, this.hexToRgba(lineColor, 0.7));
              gradient.addColorStop(1, this.hexToRgba(lineColor, 0.1));
              backgroundColor = gradient;
            }
          }

          const processedDataset = {
            ...dataset,
            label: dataset.label || `Linha ${readingIndex + 1}${datasetIndex > 0 ? " (Estimado)" : ""}`,
            borderColor: lineColor,
            backgroundColor,
            hoverBorderColor: lineColor,
            hoverBackgroundColor: lineColor,
            pointBackgroundColor: this.applyPointColors(dataset.pointBackgroundColor, lineColor),
            pointBorderColor: this.applyPointColors(dataset.pointBorderColor, lineColor),
            borderDash: dataset.borderDash || undefined,
            fill: dataset.fill !== undefined ? dataset.fill : (isEstimated ? false : true),
          };

          this.data.datasets.push(processedDataset);
        });
      });

      this.data.labels = this.labels;
    },

    render() {
      if (this.myChart) {
        this.myChart.data = this.data;
        this.myChart.update();
        return;
      }

      const ctx = document.getElementById(this.canvaID).getContext('2d');
      this.myChart = new Chart(ctx, {
        type: "line",
        data: this.data,
        options: this.chartOptions,
      });
    },

    hexToRgba(hex, alpha) {
      if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return hex || `rgba(64, 146, 255, ${alpha})`;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },

  watch: {
    readings: {
      deep: true,
      handler() {
        this.prepareDatasets();
        this.$nextTick(() => this.render());
      },
    },
    labels: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.data.labels = newValue;
          this.$nextTick(() => this.render());
        }
      },
    },
  },

  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: false,
          },
        },
        legend: {
          display: true,
          labels: {
            usePointStyle: true,
            generateLabels: (chart) => {
              const labels = Chart.defaults.global.legend.labels.generateLabels(chart);
              return labels.map((label) => {
                const dataset = chart.data.datasets[label.datasetIndex] || {};
                const color = this.resolveHexColor(
                  dataset.borderColor,
                  label.strokeStyle || label.fillStyle || "#4092FF"
                );
                return {
                  ...label,
                  fillStyle: color,
                  strokeStyle: color,
                  fontColor: color,
                };
              });
            },
          },
          onClick: (event, legendItem) => {
            const chart = this.myChart;
            if (!chart) return;
            const index = legendItem.datasetIndex;
            const meta = chart.getDatasetMeta(index);
            meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
            chart.update();
          },
        },
        title: { display: true, text: this.title || "" },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
          }],
        },
        elements: {
          line: {
            spanGaps: false, // Não conecta pontos quando há gaps (valores null) - pode ser sobrescrito por dataset individual
          },
        },
      };
    },
  },
};
</script>
